// src/components/ListingCard.jsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavoritesStore } from '../store/useFavoritesStore';
import { useCurrencyStore } from '../store/useCurrencyStore';
import FavButton from './FavButton';
import noImage from '../assets/no-image-640x400.jpg';
import { normalizeMediaUrl } from '../api/base';
import LeadModal from './quiz/LeadModal';

const Card = styled.article`
  border:1px solid var(--bdr); border-radius:16px; overflow:hidden;
  background:var(--card); box-shadow:var(--shadow);
  display:flex;
  flex-direction: column;
`;

const PicLink = styled(Link)`
  position:relative;
  display:block;
  aspect-ratio: 4/3; background:#111;
  img{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    image-rendering: auto;
  }
  &:focus-visible{ outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const PicDiv = styled.div`
  position:relative;
  display:block;
  aspect-ratio: 4/3; background:#111;
  img{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    image-rendering: auto;
  }
`;

const Box = styled.div`
  padding:10px;
  display:flex;
  flex-direction: column;
  gap:6px;
  @media (max-width:520px){
    padding:8px;
    gap:6px;
  }
`;
const HeaderRow = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:10px;
  @media (max-width:520px){
    flex-wrap: wrap;
    gap:8px;
  }
`;
const ActionsRow = styled.div`
  display:grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap:8px;
  margin-top:4px;

  .btn{
    width:100%;
    min-width:0;
  }

  @media (max-width:560px){
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width:380px){
    grid-template-columns: 1fr;
  }
`;
const H = styled(Link)`
  font-weight:700; font-size:16px; line-height:1.25;
  display:-webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow:hidden;
  height: calc(1.25em * 2 + 16px); /* Фиксированная высота для 2 строк + padding */
  flex:1;
  min-width:0;
  color: #000;
  background: var(--gold);
  opacity: 0.92;
  padding: 8px 12px;
  border-radius: 12px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-decoration: none;
  
  &:hover {
    opacity: 0.95;
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }
`;
const Sub = styled.div`
  opacity:.75; font-size:14px; 
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 1.5em; /* Фиксированная высота для района */
`;

const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DistrictLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  font-size: 13px;
  margin: 6px 0;
  
  .info-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 2.4em; /* Компактная минимальная высота */
  }
  
  .info-label {
    opacity: 0.6;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .info-value {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;
const Price = styled.div`
  font-weight:800; line-height:1.3;
  min-height: calc(1.3em * 2 + 20px); /* Высота для 2 строк + padding */
  color: #000;
  background: var(--gold);
  opacity: 0.88;
  padding: 10px 14px;
  border-radius: 12px;
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 4px;
`;

function pickCardImage(item){
  // Приоритет: оригинальные изображения для лучшего качества
  if (item?.cover_url) return normalizeMediaUrl(item.cover_url);
  if (item?.cover_thumb) return normalizeMediaUrl(item.cover_thumb);
  if (Array.isArray(item?.images) && item.images.length){
    const main = item.images.find(x => x?.is_main) || item.images[0];
    // Всегда предпочитаем оригинальное изображение для лучшего качества
    return normalizeMediaUrl(main?.url || main?.thumbnail_url || null);
  }
  return null;
}

export default function ListingCard({ item }){
  const { t } = useTranslation();
  const { favorites, toggleFavorite } = useFavoritesStore(s => ({
    favorites: s.favorites,
    toggleFavorite: s.toggleFavorite,
  }));

  // Подключаем currency store для реактивной конвертации цен
  const { currency, formatPriceFromCurrency } = useCurrencyStore(s => ({
    currency: s.currency,
    formatPriceFromCurrency: s.formatPriceFromCurrency,
  }));

  // ЕДИНЫЙ КЛЮЧ для всех мест
  const id = String(item.slug || item.id);
  const isFav = favorites.includes(id);
  const onFav = ()=> toggleFavorite(id);

  // Функция для правильного извлечения заголовка с поддержкой переводов
  const pickTitle = (it) => {
    if (!it) return 'Object';
    // Пробуем разные варианты заголовка
    return it.title_ru || it.title_en || it.title || it.name || 'Объект';
  };

  const imgSrc = pickCardImage(item) || noImage;
  const to = `/realty/${item.slug || item.id}`;

  // Безопасное извлечение данных района
  const getDistrictData = () => {
    if (!item.district && !item.district_id && !item.district_name) return null;
    
    // Если district - строка, используем её как название
    if (typeof item.district === 'string') {
      return {
        name: item.district,
        slug: item.district_slug || item.district_id || item.district?.toLowerCase().replace(/\s+/g, '-') || 'unknown'
      };
    }
    
    // Если district - объект, извлекаем данные
    if (typeof item.district === 'object' && item.district) {
      return {
        name: item.district.name || item.district.name_ru || item.district.name_en || item.district_name || 'Район',
        slug: item.district.slug || item.district_slug || item.district.id || item.district_id || 'unknown'
      };
    }
    
    // Если есть только district_name или district_id
    if (item.district_name || item.district_id) {
      return {
        name: item.district_name || `Район ${item.district_id}`,
        slug: item.district_slug || item.district_id || 'unknown'
      };
    }
    
    return null;
  };

  const districtData = getDistrictData();

  const Picture = (item.slug || item.id) ? (
    <PicLink to={to} aria-label={pickTitle(item)}>
      <img 
        src={imgSrc} 
        alt={pickTitle(item)} 
        loading="lazy" 
        decoding="async"
        style={{ 
          imageRendering: 'auto',
          filter: 'none',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)'
        }}
      />
    </PicLink>
  ) : (
    <PicDiv>
      <img 
        src={imgSrc} 
        alt={pickTitle(item)} 
        loading="lazy" 
        decoding="async"
        style={{ 
          imageRendering: 'auto',
          filter: 'none',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)'
        }}
      />
    </PicDiv>
  );

  const [openLead, setOpenLead] = React.useState(false);
  const PHONE_TEL = process.env.REACT_APP_PHONE_TEL || '+74959729797';
  const TELEGRAM_URL = process.env.REACT_APP_TELEGRAM_URL || 'https://t.me/+79309729797';

  return (
    <Card>
      {Picture}
      <Box>
        <HeaderRow>
          <H to={to}>{pickTitle(item)}</H>
          <FavButton on={isFav} onToggle={onFav} size={48} />
        </HeaderRow>

        <Sub>
          <SubRow>
            {districtData && (
              <DistrictLink to={`/districts/${encodeURIComponent(districtData.slug)}`}>
                {String(districtData.name)}
              </DistrictLink>
            )}
            {item.city && !districtData && <span>{String(item.city)}</span>}
          </SubRow>
        </Sub>

        <InfoGrid>
          <div className="info-item">
            <div className="info-label">{t('catalog.labels.installment')}</div>
            <div className="info-value">
              {(() => {
                // Проверяем наличие рассрочки
                if (!item.installment_available) {
                  return 'Нет';
                }
                
                // Если есть количество месяцев, показываем его
                if (item.installment_months && typeof item.installment_months === 'number') {
                  return `${item.installment_months} мес`;
                }
                
                // Если есть процент, показываем его
                if (item.installment_percent && typeof item.installment_percent === 'number') {
                  return `${item.installment_percent}%`;
                }
                
                // Если рассрочка доступна, но нет деталей
                return 'Есть';
              })()}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('catalog.labels.area')}</div>
            <div className="info-value">
              {(() => {
                // Проверяем диапазон площади
                const rng = item?.area_total_range || item?.area_range;
                if (rng && (rng.min != null || rng.max != null)) {
                  const aMin = rng.min;
                  const aMax = rng.max;
                  if (aMin != null && aMax != null && aMin !== aMax) return `${aMin}–${aMax} м²`;
                  const a = aMin != null ? aMin : aMax;
                  return a != null ? `${t('catalog.labels.areaFrom')} ${a} м²` : '-';
                }
                
                // Проверяем отдельные поля min/max
                const areaMin = item.area_min || item.area_total_min;
                const areaMax = item.area_max || item.area_total_max;
                if (areaMin != null && areaMax != null && areaMin !== areaMax) {
                  return `${areaMin}–${areaMax} м²`;
                }
                if (areaMin != null) {
                  return `${t('catalog.labels.areaFrom')} ${areaMin} м²`;
                }
                if (areaMax != null) {
                  return `до ${areaMax} м²`;
                }
                
                // Проверяем обычную площадь
                const area = item.area_total || item.area;
                return area ? `${area} м²` : '-';
              })()}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">Цена за м²</div>
            <div className="info-value"
              dangerouslySetInnerHTML={{
                __html: (() => {
                  const pMin = item.price_per_m2_min;
                  const pMax = item.price_per_m2_max;
                  const baseCurrency = item?.price_currency || item?.currency?.code || 'AED';
                  
                  // Если обе цены пустые или равны 0, показываем "Цена по запросу"
                  if ((!pMin || pMin === 0) && (!pMax || pMax === 0)) {
                    return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
                  }
                  
                  if (pMin != null && pMin > 0 && pMax != null && pMax > 0 && pMin !== pMax) {
                    const minFormatted = formatPriceFromCurrency(pMin, baseCurrency, currency);
                    const maxFormatted = formatPriceFromCurrency(pMax, baseCurrency, currency);
                    return `${minFormatted}–${maxFormatted}/м²`;
                  }
                  if (pMin != null && pMin > 0) {
                    const formatted = formatPriceFromCurrency(pMin, baseCurrency, currency);
                    return `от ${formatted}/м²`;
                  }
                  if (pMax != null && pMax > 0) {
                    const formatted = formatPriceFromCurrency(pMax, baseCurrency, currency);
                    return `до ${formatted}/м²`;
                  }
                  return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
                })()
              }}
            />
          </div>
          <div className="info-item">
            <div className="info-label">{t('catalog.labels.handover')}</div>
            <div className="info-value">
              {item.handover_quarter && item.handover_year 
                ? `${item.handover_quarter} ${item.handover_year}`
                : item.handover_year || '-'
              }
            </div>
          </div>
        </InfoGrid>

        <Price
          dangerouslySetInnerHTML={{
            __html: (() => {
              if (item.price_formatted) return item.price_formatted;
              
              // Проверяем наличие диапазона цен (используем ?? вместо || чтобы 0 не считался falsy)
              const priceMin = item.price ?? item.price_min ?? item.price_base ?? null;
              const priceMax = item.price_max ?? null;
              const baseCurrency = item?.price_currency || item?.currency?.code || 'AED';
              
              // Если нет ни минимальной, ни максимальной цены, показываем "Цена по запросу"
              if ((priceMin === null || priceMin === 0) && (priceMax === null || priceMax === 0)) {
                return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
              }
              
              if (priceMin != null && priceMin > 0 && priceMax != null && priceMax > 0 && priceMin !== priceMax) {
                const minFormatted = formatPriceFromCurrency(priceMin, baseCurrency, currency);
                const maxFormatted = formatPriceFromCurrency(priceMax, baseCurrency, currency);
                return `${t('catalog.labels.priceFrom')} ${minFormatted}–${maxFormatted}`;
              }
              
              // Проверяем price_range
              if (item.price_range && (item.price_range.min != null || item.price_range.max != null)) {
                const pMin = item.price_range.min;
                const pMax = item.price_range.max;
                if (pMin != null && pMin > 0 && pMax != null && pMax > 0 && pMin !== pMax) {
                  const minFormatted = formatPriceFromCurrency(pMin, baseCurrency, currency);
                  const maxFormatted = formatPriceFromCurrency(pMax, baseCurrency, currency);
                  return `${t('catalog.labels.priceFrom')} ${minFormatted}–${maxFormatted}`;
                }
                const p = pMin != null && pMin > 0 ? pMin : (pMax != null && pMax > 0 ? pMax : null);
                if (p != null) {
                  const formatted = formatPriceFromCurrency(p, baseCurrency, currency);
                  return `${t('catalog.labels.priceFrom')} ${formatted}`;
                }
              }
              
              // Одиночная цена (только если больше 0)
              if (priceMin != null && priceMin > 0) {
                const formatted = formatPriceFromCurrency(priceMin, baseCurrency, currency);
                return `${t('catalog.labels.priceFrom')} ${formatted}`;
              }
              
              // Цена по запросу с поддержкой переводов
              return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
            })()
          }}
        />
        
        {/* Цена за кв.м */}
        {(item.price_per_m2_min || item.price_per_m2_max || true) && (
          <div 
            style={{
              fontSize: '13px', 
              opacity: 0.7, 
              marginTop: '2px',
              minHeight: '1.3em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                const priceM2Min = item.price_per_m2_min;
                const priceM2Max = item.price_per_m2_max;
                const baseCurrency = item?.price_currency || item?.currency?.code || 'AED';
                
                // Если обе цены пустые или равны 0, показываем "Цена по запросу"
                if ((!priceM2Min || priceM2Min === 0) && (!priceM2Max || priceM2Max === 0)) {
                  return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
                }
                
                if (priceM2Min && priceM2Min > 0 && priceM2Max && priceM2Max > 0 && priceM2Min !== priceM2Max) {
                  const minFormatted = formatPriceFromCurrency(priceM2Min, baseCurrency, currency);
                  const maxFormatted = formatPriceFromCurrency(priceM2Max, baseCurrency, currency);
                  return `${minFormatted}–${maxFormatted}/м²`;
                }
                if (priceM2Min && priceM2Min > 0) {
                  const formatted = formatPriceFromCurrency(priceM2Min, baseCurrency, currency);
                  return `${formatted}/м²`;
                }
                if (priceM2Max && priceM2Max > 0) {
                  const formatted = formatPriceFromCurrency(priceM2Max, baseCurrency, currency);
                  return `до ${formatted}/м²`;
                }
                return t('catalog.labels.priceOnRequest') || 'Цена по запросу';
              })()
            }}
          />
        )}
        <ActionsRow>
          <a 
            className="btn" 
            href={TELEGRAM_URL} 
            target="_blank" 
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Telegram
          </a>
          <a 
            className="btn" 
            href={`tel:${PHONE_TEL}`}
            onClick={(e) => e.stopPropagation()}
          >
            Звонок
          </a>
          <button 
            className="btn" 
            onClick={(e) => {
              e.stopPropagation();
              setOpenLead(true);
            }}
          >
            Заказать звонок
          </button>
        </ActionsRow>
      </Box>
      {openLead && <LeadModal open={openLead} onClose={()=>setOpenLead(false)} />}
    </Card>
  );
}
